import React, { useState } from 'react';
import { DataTable } from '../components/DataTable';
import { LoadingBlock } from '../components/LoadingBlock';
import { SectionCard } from '../components/SectionCard';

const EMPTY_FORM = {
  time_id: '',
  full_name: '',
  nickname: '',
  cpf: '',
  rg: '',
  phone: '',
  whatsapp: '',
  email: '',
  address: '',
  bairro: '',
  city: '',
  state: '',
  cep: '',
  number: '',
  position: 'ST',
  secondary_position: '',
  foot: 'right',
  birth_date: '',
  height_cm: '',
  weight_kg: '',
  shoe_size: '',
  shirt_size: '',
  category: 'adulto',
  play_style: '',
  is_captain: false,
  takes_penalty: false,
  takes_free_kick: false,
  photo_url: '',
  overall: '60',
  velocidade: '60',
  forca: '60',
  resistencia: '60',
  passe: '60',
  finalizacao: '60',
  drible: '60',
  defesa: '60',
  cabeceio: '60',
  lideranca: '60',
  disciplina: '60',
  status_fisico: 'ok',
  lgpd_image_consent: false,
  lgpd_video_consent: false,
  lgpd_public_profile_consent: false,
  lgpd_photo_sales_consent: false,
  guardian_name: '',
  guardian_document: '',
  guardian_phone: '',
  guardian_email: '',
  guardian_relationship: '',
  guardian_authorization: false,
  active: true,
};

const POSITION_OPTIONS = ['GK', 'RB', 'CB', 'LB', 'DM', 'CM', 'AM', 'RW', 'LW', 'ST'];
const PROFILE_PLACEHOLDER = `${process.env.PUBLIC_URL || ''}/profile-placeholder.png`;

export function PlayersPage({
  loading,
  error,
  data,
  onCreatePlayer,
  onUpdatePlayer,
  onDeletePlayer,
  onUploadPlayerImage,
  canManage,
  creating,
  updating,
  uploading,
  deleting,
}) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [imageMessage, setImageMessage] = useState('');
  const isEditing = Boolean(editingId);
  const teams = data?.teams || [];

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setSelectedFile(null);
    setImageMessage('');
  };

  const uploadSelectedImage = async () => {
    if (!selectedFile || !onUploadPlayerImage) {
      return;
    }

    const url = await onUploadPlayerImage(selectedFile, editingId);
    if (url) {
      setForm((current) => ({
        ...current,
        photo_url: url,
        lgpd_image_consent: true,
      }));
      setSelectedFile(null);
      setImageMessage('Imagem enviada. Salve o jogador para aplicar no elenco.');
    }
  };

  const removeCurrentImage = () => {
    setSelectedFile(null);
    setForm((current) => ({
      ...current,
      photo_url: '',
      lgpd_image_consent: false,
    }));
    setImageMessage('Foto removida. Salve o jogador para voltar ao avatar padrão.');
  };

  if (loading) return <LoadingBlock label="Carregando jogadores..." />;
  if (error) return <div className="page-error">{error}</div>;

  return (
    <div className="page-grid two-columns">
      <SectionCard
        title="Jogadores"
        subtitle="Controle elenco, reservas, atributos e disponibilidade física."
        actions={canManage ? (
          <div className="inline-actions">
            <button className="ghost-button" type="button" onClick={resetForm}>Novo jogador</button>
            {isEditing ? <button className="ghost-button" type="button" onClick={resetForm}>Cancelar edição</button> : null}
          </div>
        ) : null}
      >
        {canManage ? (
          <form
            className="admin-form"
            onSubmit={(event) => {
              event.preventDefault();
              const payload = {
                ...form,
                time_id: Number(form.time_id),
                number: form.number === '' ? null : Number(form.number),
                height_cm: form.height_cm === '' ? null : Number(form.height_cm),
                weight_kg: form.weight_kg === '' ? null : Number(form.weight_kg),
                shoe_size: form.shoe_size === '' ? null : Number(form.shoe_size),
                overall: Number(form.overall),
                velocidade: Number(form.velocidade),
                forca: Number(form.forca),
                resistencia: Number(form.resistencia),
                passe: Number(form.passe),
                finalizacao: Number(form.finalizacao),
                drible: Number(form.drible),
                defesa: Number(form.defesa),
                cabeceio: Number(form.cabeceio),
                lideranca: Number(form.lideranca),
                disciplina: Number(form.disciplina),
                active: Boolean(form.active),
              };

              if (isEditing) {
                onUpdatePlayer(editingId, payload).then?.(resetForm);
                return;
              }

              onCreatePlayer(payload).then?.(resetForm);
            }}
          >
            <div className="callout-box compact-box">
              <span className="eyebrow">Acesso do administrador</span>
              <strong>Cadastro, edição e exclusão liberados</strong>
              <p>Como admin, você adiciona, edita e remove jogadores, define time, atributos, status físico e foto do atleta.</p>
            </div>
            <div className="media-uploader">
              <div className="media-preview">
                <img
                  src={form.photo_url || PROFILE_PLACEHOLDER}
                  alt={`Foto de ${form.nickname || form.full_name || 'jogador'}`}
                  onError={(event) => {
                    event.currentTarget.src = PROFILE_PLACEHOLDER;
                  }}
                />
              </div>
              <div className="media-uploader__controls">
                <label>
                  Foto do jogador
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={(event) => setSelectedFile(event.target.files?.[0] || null)}
                  />
                </label>
                <button className="ghost-button" type="button" onClick={uploadSelectedImage} disabled={!selectedFile || uploading}>
                  {uploading ? 'Enviando...' : 'Enviar imagem'}
                </button>
                <div className="inline-actions">
                  <button className="inline-button" type="button" onClick={removeCurrentImage} disabled={!form.photo_url && !selectedFile}>
                    Remover foto
                  </button>
                  {form.photo_url ? (
                    <a className="inline-button media-link" href={form.photo_url} target="_blank" rel="noreferrer">
                      Abrir foto
                    </a>
                  ) : null}
                </div>
                <small>JPG, PNG ou WEBP até 5 MB. A foto alimenta escalação, ranking e perfil.</small>
                {imageMessage ? <small className="success-note compact">{imageMessage}</small> : null}
              </div>
            </div>
            <div className="form-row">
              <label>
                Time
                <select value={form.time_id} onChange={(event) => setForm({ ...form, time_id: event.target.value })} required>
                  <option value="">Selecione</option>
                  {teams.map((team) => <option key={team.id} value={team.id}>{team.name}</option>)}
                </select>
              </label>
              <label>
                Nome
                <input value={form.full_name} onChange={(event) => setForm({ ...form, full_name: event.target.value })} required />
              </label>
            </div>
            <div className="form-row triple">
              <label>
                CPF
                <input value={form.cpf} onChange={(event) => setForm({ ...form, cpf: event.target.value })} />
              </label>
              <label>
                RG
                <input value={form.rg} onChange={(event) => setForm({ ...form, rg: event.target.value })} />
              </label>
              <label>
                Email
                <input type="email" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} />
              </label>
            </div>
            <div className="form-row triple">
              <label>
                Telefone
                <input value={form.phone} onChange={(event) => setForm({ ...form, phone: event.target.value })} />
              </label>
              <label>
                WhatsApp
                <input value={form.whatsapp} onChange={(event) => setForm({ ...form, whatsapp: event.target.value })} />
              </label>
              <label>
                Bairro
                <input value={form.bairro} onChange={(event) => setForm({ ...form, bairro: event.target.value })} />
              </label>
            </div>
            <div className="form-row triple">
              <label>
                Endereço
                <input value={form.address} onChange={(event) => setForm({ ...form, address: event.target.value })} />
              </label>
              <label>
                Cidade
                <input value={form.city} onChange={(event) => setForm({ ...form, city: event.target.value })} />
              </label>
              <label>
                Estado
                <input maxLength="2" value={form.state} onChange={(event) => setForm({ ...form, state: event.target.value.toUpperCase() })} />
              </label>
            </div>
            <div className="form-row">
              <label>
                CEP
                <input value={form.cep} onChange={(event) => setForm({ ...form, cep: event.target.value })} />
              </label>
            </div>
            <div className="form-row triple">
              <label>
                Apelido
                <input value={form.nickname} onChange={(event) => setForm({ ...form, nickname: event.target.value })} />
              </label>
              <label>
                Camisa
                <input type="number" min="1" max="99" value={form.number} onChange={(event) => setForm({ ...form, number: event.target.value })} />
              </label>
              <label>
                Posição
                <select value={form.position} onChange={(event) => setForm({ ...form, position: event.target.value })}>
                  {POSITION_OPTIONS.map((position) => <option key={position} value={position}>{position}</option>)}
                </select>
              </label>
              <label>
                Posição secundária
                <select value={form.secondary_position} onChange={(event) => setForm({ ...form, secondary_position: event.target.value })}>
                  <option value="">Sem secundária</option>
                  {POSITION_OPTIONS.map((position) => <option key={position} value={position}>{position}</option>)}
                </select>
              </label>
            </div>
            <div className="form-row triple">
              <label>
                Pé
                <select value={form.foot} onChange={(event) => setForm({ ...form, foot: event.target.value })}>
                  <option value="right">Direito</option>
                  <option value="left">Esquerdo</option>
                  <option value="both">Ambos</option>
                </select>
              </label>
              <label>
                Status físico
                <select value={form.status_fisico} onChange={(event) => setForm({ ...form, status_fisico: event.target.value })}>
                  <option value="ok">OK</option>
                  <option value="warning">Alerta</option>
                  <option value="injured">Lesionado</option>
                  <option value="suspended">Suspenso</option>
                </select>
              </label>
              <label>
                Categoria
                <select value={form.category} onChange={(event) => setForm({ ...form, category: event.target.value })}>
                  <option value="sub_15">Sub-15</option>
                  <option value="sub_17">Sub-17</option>
                  <option value="sub_20">Sub-20</option>
                  <option value="adulto">Adulto</option>
                  <option value="veterano">Veterano</option>
                </select>
              </label>
            </div>
            <div className="form-row triple">
              <label>
                Nascimento
                <input type="date" value={form.birth_date} onChange={(event) => setForm({ ...form, birth_date: event.target.value })} />
              </label>
              <label>
                Altura cm
                <input type="number" min="80" max="230" value={form.height_cm} onChange={(event) => setForm({ ...form, height_cm: event.target.value })} />
              </label>
              <label>
                Peso kg
                <input type="number" min="20" max="200" step="0.1" value={form.weight_kg} onChange={(event) => setForm({ ...form, weight_kg: event.target.value })} />
              </label>
            </div>
            <div className="form-row triple">
              <label>
                Calçado
                <input type="number" min="25" max="55" value={form.shoe_size} onChange={(event) => setForm({ ...form, shoe_size: event.target.value })} />
              </label>
              <label>
                Camisa
                <select value={form.shirt_size} onChange={(event) => setForm({ ...form, shirt_size: event.target.value })}>
                  <option value="">Não informado</option>
                  {['PP', 'P', 'M', 'G', 'GG', 'XG'].map((size) => <option key={size} value={size}>{size}</option>)}
                </select>
              </label>
              <label>
                Estilo de jogo
                <input value={form.play_style} onChange={(event) => setForm({ ...form, play_style: event.target.value })} />
              </label>
            </div>
            <div className="form-row triple">
              <label>
                Overall
                <input type="number" min="1" max="99" value={form.overall} onChange={(event) => setForm({ ...form, overall: event.target.value })} />
              </label>
              <label>
                Velocidade
                <input type="number" min="1" max="99" value={form.velocidade} onChange={(event) => setForm({ ...form, velocidade: event.target.value })} />
              </label>
              <label>
                Força
                <input type="number" min="1" max="99" value={form.forca} onChange={(event) => setForm({ ...form, forca: event.target.value })} />
              </label>
            </div>
            <div className="form-row triple">
              <label>
                Resistência
                <input type="number" min="1" max="99" value={form.resistencia} onChange={(event) => setForm({ ...form, resistencia: event.target.value })} />
              </label>
              <label>
                Passe
                <input type="number" min="1" max="99" value={form.passe} onChange={(event) => setForm({ ...form, passe: event.target.value })} />
              </label>
              <label>
                Finalização
                <input type="number" min="1" max="99" value={form.finalizacao} onChange={(event) => setForm({ ...form, finalizacao: event.target.value })} />
              </label>
            </div>
            <div className="form-row">
              <label>
                Defesa
                <input type="number" min="1" max="99" value={form.defesa} onChange={(event) => setForm({ ...form, defesa: event.target.value })} />
              </label>
              <label>
                Drible
                <input type="number" min="1" max="99" value={form.drible} onChange={(event) => setForm({ ...form, drible: event.target.value })} />
              </label>
            </div>
            <div className="form-row triple">
              <label>
                Cabeceio
                <input type="number" min="1" max="99" value={form.cabeceio} onChange={(event) => setForm({ ...form, cabeceio: event.target.value })} />
              </label>
              <label>
                Liderança
                <input type="number" min="1" max="99" value={form.lideranca} onChange={(event) => setForm({ ...form, lideranca: event.target.value })} />
              </label>
              <label>
                Disciplina
                <input type="number" min="1" max="99" value={form.disciplina} onChange={(event) => setForm({ ...form, disciplina: event.target.value })} />
              </label>
            </div>
            <div className="form-row triple">
              <label className="checkbox-field">
                <span>Capitão</span>
                <input type="checkbox" checked={Boolean(form.is_captain)} onChange={(event) => setForm({ ...form, is_captain: event.target.checked })} />
              </label>
              <label className="checkbox-field">
                <span>Bate pênalti</span>
                <input type="checkbox" checked={Boolean(form.takes_penalty)} onChange={(event) => setForm({ ...form, takes_penalty: event.target.checked })} />
              </label>
              <label className="checkbox-field">
                <span>Bate falta</span>
                <input type="checkbox" checked={Boolean(form.takes_free_kick)} onChange={(event) => setForm({ ...form, takes_free_kick: event.target.checked })} />
              </label>
            </div>
            <div className="form-row">
              <label>
                Foto do jogador
                <input value={form.photo_url} onChange={(event) => setForm({ ...form, photo_url: event.target.value })} />
              </label>
            </div>
            <div className="callout-box compact-box admin-callout">
              <span className="eyebrow">Autorizações obrigatórias</span>
              <strong>Vídeo/transmissão e perfil público liberam o atleta ativo</strong>
              <p>A venda de fotos é separada e opcional neste início, para não travar o cadastro dos campeonatos.</p>
            </div>
            <div className="form-row triple">
              <label className="checkbox-field">
                <span>Uso de imagem no Perifa</span>
                <input type="checkbox" checked={Boolean(form.lgpd_image_consent)} onChange={(event) => setForm({ ...form, lgpd_image_consent: event.target.checked })} />
              </label>
              <label className="checkbox-field">
                <span>Vídeo/transmissão autorizado</span>
                <input type="checkbox" checked={Boolean(form.lgpd_video_consent)} onChange={(event) => setForm({ ...form, lgpd_video_consent: event.target.checked })} />
              </label>
              <label className="checkbox-field">
                <span>Perfil público autorizado</span>
                <input type="checkbox" checked={Boolean(form.lgpd_public_profile_consent)} onChange={(event) => setForm({ ...form, lgpd_public_profile_consent: event.target.checked })} />
              </label>
            </div>
            <div className="form-row">
              <label className="checkbox-field">
                <span>Venda de fotos autorizada</span>
                <input type="checkbox" checked={Boolean(form.lgpd_photo_sales_consent)} onChange={(event) => setForm({ ...form, lgpd_photo_sales_consent: event.target.checked })} />
              </label>
            </div>
            <div className="form-row triple">
              <label>
                Responsável legal
                <input value={form.guardian_name} onChange={(event) => setForm({ ...form, guardian_name: event.target.value })} />
              </label>
              <label>
                Documento do responsável
                <input value={form.guardian_document} onChange={(event) => setForm({ ...form, guardian_document: event.target.value })} />
              </label>
              <label>
                Telefone do responsável
                <input value={form.guardian_phone} onChange={(event) => setForm({ ...form, guardian_phone: event.target.value })} />
              </label>
            </div>
            <div className="form-row triple">
              <label>
                Email do responsável
                <input type="email" value={form.guardian_email} onChange={(event) => setForm({ ...form, guardian_email: event.target.value })} />
              </label>
              <label>
                Parentesco
                <input value={form.guardian_relationship} onChange={(event) => setForm({ ...form, guardian_relationship: event.target.value })} />
              </label>
              <label className="checkbox-field">
                <span>Autorização do responsável</span>
                <input type="checkbox" checked={Boolean(form.guardian_authorization)} onChange={(event) => setForm({ ...form, guardian_authorization: event.target.checked })} />
              </label>
            </div>
            <div className="form-row">
              <label className="checkbox-field">
                <span>Disponível no elenco</span>
                <input type="checkbox" checked={Boolean(form.active)} onChange={(event) => setForm({ ...form, active: event.target.checked })} />
              </label>
            </div>
            <button className="primary-button" type="submit" disabled={creating || updating}>
              {isEditing ? (updating ? 'Salvando...' : 'Atualizar jogador') : (creating ? 'Criando...' : 'Criar jogador')}
            </button>
          </form>
        ) : (
          <div className="empty-state">Seu perfil não pode editar elenco.</div>
        )}
      </SectionCard>

      <SectionCard title="Lista de jogadores" subtitle="Os reservas e titulares passam a ser organizados em cima deste cadastro.">
        <DataTable
          rows={data?.items || []}
          columns={[
            {
              key: 'photo_url',
              label: 'Foto',
              render: (row) => (
                <img
                  className="table-avatar"
                  src={row.photo_url || PROFILE_PLACEHOLDER}
                  alt={`Foto de ${row.nickname || row.full_name || 'jogador'}`}
                  onError={(event) => {
                    event.currentTarget.src = PROFILE_PLACEHOLDER;
                  }}
                />
              ),
            },
            { key: 'full_name', label: 'Jogador' },
            { key: 'team_name', label: 'Time' },
            { key: 'position', label: 'Posição' },
            { key: 'number', label: 'Camisa' },
            { key: 'overall', label: 'Overall' },
            { key: 'status_fisico', label: 'Status físico', type: 'status' },
            {
              key: 'actions',
              label: 'Ações',
              render: (row) => (
                <div className="inline-actions">
                  {canManage ? (
                    <>
                      <button
                        className="inline-button"
                        type="button"
                        onClick={() => {
                          setEditingId(row.id);
                          setSelectedFile(null);
                          setImageMessage('');
                          setForm({
                            time_id: String(row.time_id || ''),
                            full_name: row.full_name || '',
                            nickname: row.nickname || '',
                            cpf: row.cpf || '',
                            rg: row.rg || '',
                            phone: row.phone || '',
                            whatsapp: row.whatsapp || '',
                            email: row.email || '',
                            address: row.address || '',
                            bairro: row.bairro || '',
                            city: row.city || '',
                            state: row.state || '',
                            cep: row.cep || '',
                            number: row.number ?? '',
                            position: row.position || 'ST',
                            secondary_position: row.secondary_position || '',
                            foot: row.foot || 'right',
                            birth_date: row.birth_date || '',
                            height_cm: row.height_cm ?? '',
                            weight_kg: row.weight_kg ?? '',
                            shoe_size: row.shoe_size ?? '',
                            shirt_size: row.shirt_size || '',
                            category: row.category || 'adulto',
                            play_style: row.play_style || '',
                            is_captain: Boolean(row.is_captain),
                            takes_penalty: Boolean(row.takes_penalty),
                            takes_free_kick: Boolean(row.takes_free_kick),
                            photo_url: row.photo_url || '',
                            overall: String(row.overall ?? 60),
                            velocidade: String(row.velocidade ?? 60),
                            forca: String(row.forca ?? 60),
                            resistencia: String(row.resistencia ?? 60),
                            passe: String(row.passe ?? 60),
                            finalizacao: String(row.finalizacao ?? 60),
                            drible: String(row.drible ?? 60),
                            defesa: String(row.defesa ?? 60),
                            cabeceio: String(row.cabeceio ?? 60),
                            lideranca: String(row.lideranca ?? 60),
                            disciplina: String(row.disciplina ?? 60),
                            status_fisico: row.status_fisico || 'ok',
                            lgpd_image_consent: Boolean(row.lgpd_image_consent_at),
                            lgpd_video_consent: Boolean(row.lgpd_video_consent_at),
                            lgpd_public_profile_consent: Boolean(row.lgpd_public_profile_consent_at),
                            lgpd_photo_sales_consent: Boolean(row.lgpd_photo_sales_consent_at),
                            guardian_name: row.guardian_name || '',
                            guardian_document: row.guardian_document || '',
                            guardian_phone: row.guardian_phone || '',
                            guardian_email: row.guardian_email || '',
                            guardian_relationship: row.guardian_relationship || '',
                            guardian_authorization: Boolean(row.guardian_authorization_at),
                            active: Boolean(row.active),
                          });
                        }}
                      >
                        Editar
                      </button>
                      <button className="inline-button danger-text" type="button" onClick={() => onDeletePlayer(row.id)} disabled={deleting}>
                        Remover
                      </button>
                    </>
                  ) : (
                    <span className="table-muted">Somente leitura</span>
                  )}
                </div>
              ),
            },
          ]}
        />
      </SectionCard>
    </div>
  );
}
